import { t, type I18nLocale } from '@ardoq/i18n';
import {
  formattingLegendRow,
  formattingSections,
  type ActiveConditionalFormattingForLegend,
  type ViewLegendRowModel,
  type ViewLegendSection,
} from '@ardoq/view-legend';
import { isTruthy } from '@ardoq/common-helpers';
import {
  createColorScale,
  toCssColor,
  type SequentialRampName,
  type SurfaceColoring,
} from '@ardoq/xyz-charts';

/** Stops sampled along a continuous ramp, enough for a swatch to show its curve. */
const CONTINUOUS_RAMP_STOPS = 8;

/** A CSS gradient through a ramp, stepped when the ramp is banded. */
export const toRampGradient = (
  ramp: SequentialRampName,
  bands: number
): string => {
  const scale = createColorScale({ range: [0, 1], ramp, bands });
  const stops =
    bands > 0
      ? Array.from({ length: bands }, (_, band) => {
          const color = toCssColor(scale.at((band + 0.5) / bands));
          return `${color} ${(band / bands) * 100}% ${((band + 1) / bands) * 100}%`;
        })
      : Array.from({ length: CONTINUOUS_RAMP_STOPS }, (_, index) => {
          const fraction = index / (CONTINUOUS_RAMP_STOPS - 1);
          return `${toCssColor(scale.at(fraction))} ${fraction * 100}%`;
        });
  return `linear-gradient(to right, ${stops.join(', ')})`;
};

const RAMP_SWATCH_ASPECT = 3;

export type FieldColoringLegendArgs = {
  coloring: SurfaceColoring;
  activeConditionalFormatting: ActiveConditionalFormattingForLegend;
  zAxisTitle: string;
  ramp: SequentialRampName;
  bands: number;
  /** The gray the system rule paints components no rule colors, when that rule is on. */
  unformattedColor: string | null;
  /** The gray the chart paints where the coloring gives none, as CSS. */
  unknownColor: string;
};

const toHeightRow = (
  { zAxisTitle, ramp, bands }: FieldColoringLegendArgs,
  i18nLocale: I18nLocale
): ViewLegendRowModel => ({
  id: 'surface3dColor',
  title: t(i18nLocale, 'views:surface3d.legend.color', 'Color'),
  sections: [
    {
      heading: null,
      items: [
        {
          id: 'surface3dHeightRamp',
          label: zAxisTitle,
          getShape: size => (
            <span
              style={{
                display: 'inline-block',
                width: size * RAMP_SWATCH_ASPECT,
                height: size,
                borderRadius: 2,
                background: toRampGradient(ramp, bands),
              }}
            />
          ),
        },
      ],
    },
  ],
});

const toNoMatchingRuleSection = (
  { coloring, unformattedColor, unknownColor }: FieldColoringLegendArgs,
  i18nLocale: I18nLocale
): ViewLegendSection => ({
  heading: null,
  items: [
    {
      id: 'surface3dNoMatchingRule',
      color:
        coloring.kind === 'regions' && unformattedColor
          ? unformattedColor
          : unknownColor,
      label: t(
        i18nLocale,
        'views:surface3d.legend.noMatchingRule',
        'No matching rule'
      ),
    },
  ],
});

const toFormattingRows = (
  args: FieldColoringLegendArgs,
  i18nLocale: I18nLocale
): ViewLegendRowModel[] =>
  [
    formattingLegendRow({
      formatting: [
        ...(formattingSections(args.activeConditionalFormatting, false) ?? []),
        toNoMatchingRuleSection(args, i18nLocale),
      ],
      i18nLocale,
    }),
  ].filter(isTruthy);

/**
 * The surface's legend: what its color shows.
 *
 * By height it is the ramp, named for the height field - the axis already carries the values, so
 * the swatch only has to say which field the color follows. By conditional formatting it is the
 * rules, as every other view lists them, and the gray the surface takes where no rule matches.
 */
export const toFieldColoringLegendRows = (
  args: FieldColoringLegendArgs,
  i18nLocale: I18nLocale
): ViewLegendRowModel[] =>
  args.coloring.kind === 'height'
    ? [toHeightRow(args, i18nLocale)]
    : toFormattingRows(args, i18nLocale);
